import { Select, Space, TableProps } from "antd";
import dayjs from "dayjs";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetLeaveApplicationListQuery } from "../../../features/leaveApplication/leaveApplicationApi";
import { useGetLeaveTypeListQuery } from "../../../features/leaveType/leaveTypeApi";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import { formatDate } from "../../../lib/time";
import { leaveStatusList } from "../../../pages/admin/Leave/LeaveRequests";
import { GetLeaveApplicationParams, ModalComponentProps } from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../utils/utils";
import CustomModal from "../../ui/CustomModal";
import CustomTable from "../../ui/CustomTable";

enum Status {
  accepted = "status-success",
  pending = "status-warning",
  rejected = "status-danger",
  canceled = "status-danger",
}

export default function LeaveHistoryModal({
  data: viewId,
  ...rest
}: ModalComponentProps<number>) {
  const { page, limit, handlePagination, handleLimit, filter, handleFilter } =
    useTable<GetLeaveApplicationParams>();
  const {
    data: leaveTypeListData,
    isLoading: isLeaveTypeListLoading,
    isError: isLeaveTypeError,
    error: leaveTypeError,
  } = useGetLeaveTypeListQuery({
    params: {
      is_published: 1,
    },
  });
  const {
    isLoading: isLeaveLoading,
    isError: isLeaveError,
    error: leaveError,
    data: leaveData,
  } = useGetLeaveApplicationListQuery(
    {
      params: {
        business_member_id: Number(viewId),
        ...filter,
      },
    },
    {
      skip: !viewId,
    }
  );
  useError(isLeaveTypeError, leaveTypeError, "Get leave type fail");
  useError(isLeaveError, leaveError, "Get leave history fail");

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Applied Date",
      dataIndex: "created_at",
      render: (val) => (val ? formatDate(val) : "n/a"),
      sorter: (a, b) =>
        dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
      sortIcon: () => <SortIcon />,
    },
    {
      title: "Leave Period",
      render: (val) =>
        val?.start_date === val?.end_date ? (
          dayjs(val?.start_date, dbDateFormat).format(dateFormat)
        ) : (
          <span>
            {dayjs(val?.start_date, dbDateFormat).format(dateFormat)} - <br />{" "}
            {dayjs(val?.end_date, dbDateFormat).format(dateFormat)}
          </span>
        ),
    },
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      render: (val) => val?.title || "n/a",
    },

    {
      title: "Day",
      dataIndex: "total_days",
      align: "center",
      render: (val) => (val || 0) + " Days",
      sorter: (a, b) => Number(a?.total_days) - Number(b?.total_days),
      sortIcon: () => <SortIcon />,
    },
    {
      title: "Leave Status",
      dataIndex: "status",
      render: (value: keyof typeof Status) => (
        <div className="flex items-center gap-1">
          <span className={Status[value]}>
            {capitalize(value === "accepted" ? "approved" : value)}
          </span>
          {/* <Button
            className="action-button border-0"
            size="small"
            icon={<InfoIcon />}
          /> */}
        </div>
      ),
    },
  ];

  return (
    <CustomModal
      footer={null}
      closeIcon={null}
      width={750}
      className="modal-padding-hide"
      {...rest}
    >
      <Space
        wrap
        className="p-4 w-full justify-between sticky top-0 z-10 bg-white"
      >
        <h4 className="text-sub-header text-[22px] font-semibold leading-[33px]">
          Leave History
        </h4>
        <Space wrap size={10}>
          <Select
            allowClear
            showSearch
            onChange={(val) => handleFilter("leave_type_id", val)}
            placeholder="Leave Type"
            filterOption={selectSearchOption}
            options={
              selectArrayOption(
                leaveTypeListData?.data?.leave_types,
                "title",
                "id"
              ) || []
            }
            className="min-w-[140px]"
            suffixIcon={<SelectDownIcon />}
            rootClassName="icon-gray"
            loading={isLeaveTypeListLoading}
          />
          <Select
            allowClear
            showSearch
            onChange={(val) => handleFilter("status", val)}
            placeholder="Leave Status"
            filterOption={selectSearchOption}
            options={leaveStatusList}
            className="min-w-[90px]"
            suffixIcon={<SelectDownIcon />}
            rootClassName="icon-gray"
          />
        </Space>
      </Space>

      <CustomTable
        loading={isLeaveLoading}
        limit={limit}
        page={page}
        columns={columns}
        dataSource={addKeyInArray(leaveData?.data?.leave_applications)}
        total={leaveData?.data?.total_leave_applications}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        hideRowSelect
      />
    </CustomModal>
  );
}
